// Cycle Count Setup - Manual or ABC-Based
import React, { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Combobox, ComboboxOption } from "@/components/ui/combobox";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  ArrowLeft,
  ArrowRight,
  Info,
  BarChart3,
  Calendar as CalendarIcon,
  Users,
  Sparkles,
  Target,
  Clock,
  Play,
  Save,
} from "lucide-react";
import { useNavigation } from "@/components/layout/app-layout";
import { format } from "date-fns";
import { mockWarehouses } from "@/data/mockWarehousesLocations";
import { toast } from "sonner";
import {
  saveStockTake,
  generateStockTakeId,
  generateStockTakeReference,
  SavedStockTake,
} from "@/lib/stock-take-storage";

export function StockTakesCycleCountSetupPage() {
  const { navigateTo } = useNavigation();
  const [countType, setCountType] = useState<"abc-based" | "manual">("abc-based");
  const [startOption, setStartOption] = useState<"now" | "scheduled">("now");
  const [formData, setFormData] = useState({
    description: `Cycle Count - ${format(new Date(), "MMM dd, yyyy")}`,
    date: new Date(),
    scheduledDate: new Date(),
    warehouse: "",
    abcClass: "A",
    notes: "",
    assignedTo: "",
  });

  // Prepare warehouse options for combobox
  const warehouseOptions: ComboboxOption[] = [
    { value: "all", label: "All Warehouses", description: "Count all warehouses" },
    ...mockWarehouses
      .filter((w) => w.active)
      .map((warehouse) => ({
        value: warehouse.code,
        label: warehouse.name,
        description: warehouse.code,
      })),
  ];

  // Prepare user options for combobox
  const userOptions: ComboboxOption[] = [
    { value: "john-smith", label: "John Smith", description: "Warehouse Manager" },
    { value: "jane-doe", label: "Jane Doe", description: "Inventory Specialist" },
    { value: "mike-chen", label: "Mike Chen", description: "Stock Controller" },
    { value: "sarah-johnson", label: "Sarah Johnson", description: "Operations Lead" },
  ];

  const handleContinue = () => {
    // Store form data and navigate to count page or schedule
    const cycleCountData = {
      countType,
      formData: {
        ...formData,
        abcClass: countType === "abc-based" ? formData.abcClass : undefined,
      },
      status: startOption === "now" ? "In Progress" : "Scheduled",
      scheduledDate: startOption === "scheduled" ? formData.scheduledDate : undefined,
    };
    console.log("Cycle count setup:", cycleCountData);
    
    if (startOption === "now") {
      // Navigate to count page
      navigateTo("stock-takes/cycle-count/count", { setupData: cycleCountData });
    } else {
      // Schedule the cycle count and return to list
      // TODO: Save scheduled cycle count to backend
      navigateTo("stock-takes");
    }
  };

  const handleSaveAndExit = () => {
    // Validation - description is required
    if (!formData.description) {
      toast.error("Please enter a description before saving");
      return;
    }

    try {
      const stockTakeId = generateStockTakeId();
      const reference = generateStockTakeReference();
      
      const savedStockTake: SavedStockTake = {
        id: stockTakeId,
        reference,
        description: formData.description,
        type: 'Cycle Count',
        status: 'Draft',
        currentStep: 1,
        createdAt: new Date(),
        lastModified: new Date(),
        createdBy: 'Current User', // Would come from auth context
        setupData: {
          countDate: format(startOption === "now" ? formData.date : formData.scheduledDate, "yyyy-MM-dd"),
          warehouse: formData.warehouse,
          assignedTo: formData.assignedTo,
        },
      };
      
      saveStockTake(savedStockTake);
      toast.success("Stock take saved successfully! You can resume it later from the Stock Takes page.");
      navigateTo("stock-takes");
    } catch (error) {
      toast.error("Failed to save stock take. Please try again.");
    }
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigateTo("stock-takes-home")}
          className="gap-2"
        >
          <ArrowLeft className="h-4 w-4" />
          Back
        </Button>
      </div>

      {/* Title Section */}
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <div className="p-2 rounded-lg bg-orange-50 dark:bg-orange-900/20">
            <BarChart3 className="h-6 w-6 text-orange-600 dark:text-orange-400" />
          </div>
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Cycle Count</h1>
            <p className="text-muted-foreground">
              ABC-based continuous counting or manual cycle count
            </p>
          </div>
        </div>
      </div>

      {/* Start Option Selection */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>When to Start</CardTitle>
          <CardDescription>
            Choose whether to start counting now or schedule for later
          </CardDescription>
        </CardHeader>
        <CardContent>
          <RadioGroup value={startOption} onValueChange={(value: any) => setStartOption(value)}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Label
                htmlFor="start-now"
                className={`relative flex items-start space-x-3 rounded-lg border-2 p-4 cursor-pointer transition-all ${
                  startOption === "now"
                    ? "border-orange-500 bg-orange-50 dark:bg-orange-900/20"
                    : "border-gray-200 dark:border-gray-800 hover:border-gray-300 dark:hover:border-gray-700"
                }`}
              >
                <RadioGroupItem value="now" id="start-now" className="mt-0.5" />
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <Play className="h-4 w-4 text-orange-600 dark:text-orange-400" />
                    <span className="font-semibold">Start Now</span>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Begin counting immediately after setup
                  </p>
                </div>
              </Label>

              <Label
                htmlFor="start-scheduled"
                className={`relative flex items-start space-x-3 rounded-lg border-2 p-4 cursor-pointer transition-all ${
                  startOption === "scheduled"
                    ? "border-orange-500 bg-orange-50 dark:bg-orange-900/20"
                    : "border-gray-200 dark:border-gray-800 hover:border-gray-300 dark:hover:border-gray-700"
                }`}
              >
                <RadioGroupItem value="scheduled" id="start-scheduled" className="mt-0.5" />
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <Clock className="h-4 w-4 text-orange-600 dark:text-orange-400" />
                    <span className="font-semibold">Schedule for Later</span>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Schedule this cycle count for a future date
                  </p>
                </div>
              </Label>
            </div>
          </RadioGroup>
        </CardContent>
      </Card>

      {/* Count Type Selection */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Choose Cycle Count Type</CardTitle>
          <CardDescription>
            Select whether to use ABC classification or manually select items
          </CardDescription>
        </CardHeader>
        <CardContent>
          <RadioGroup value={countType} onValueChange={(value: any) => setCountType(value)}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* ABC-Based Option */}
              <Label
                htmlFor="abc-based"
                className={`relative flex items-start space-x-3 rounded-lg border-2 p-4 cursor-pointer transition-all ${
                  countType === "abc-based"
                    ? "border-orange-500 bg-orange-50 dark:bg-orange-900/20"
                    : "border-gray-200 dark:border-gray-800 hover:border-gray-300 dark:hover:border-gray-700"
                }`}
              >
                <RadioGroupItem value="abc-based" id="abc-based" className="mt-0.5" />
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <Sparkles className="h-4 w-4 text-orange-600 dark:text-orange-400" />
                    <span className="font-semibold">ABC-Based (Recommended)</span>
                    <Badge variant="secondary" className="text-xs">Automated</Badge>
                  </div>
                  <p className="text-sm text-muted-foreground mb-3">
                    System automatically selects items based on ABC classification and due dates
                  </p>
                  <ul className="space-y-1 text-sm text-muted-foreground">
                    <li className="flex items-start gap-2">
                      <div className="h-1.5 w-1.5 rounded-full bg-current mt-1.5 flex-shrink-0" />
                      <span>A-Class items: High-value, counted monthly</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <div className="h-1.5 w-1.5 rounded-full bg-current mt-1.5 flex-shrink-0" />
                      <span>B-Class items: Medium-value, counted quarterly</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <div className="h-1.5 w-1.5 rounded-full bg-current mt-1.5 flex-shrink-0" />
                      <span>C-Class items: Low-value, counted annually</span>
                    </li>
                  </ul>
                </div>
              </Label>

              {/* Manual Option */}
              <Label
                htmlFor="manual"
                className={`relative flex items-start space-x-3 rounded-lg border-2 p-4 cursor-pointer transition-all ${
                  countType === "manual"
                    ? "border-orange-500 bg-orange-50 dark:bg-orange-900/20"
                    : "border-gray-200 dark:border-gray-800 hover:border-gray-300 dark:hover:border-gray-700"
                }`}
              >
                <RadioGroupItem value="manual" id="manual" className="mt-0.5" />
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <Target className="h-4 w-4 text-orange-600 dark:text-orange-400" />
                    <span className="font-semibold">Manual Selection</span>
                  </div>
                  <p className="text-sm text-muted-foreground mb-3">
                    Manually choose which items to include in this cycle count
                  </p>
                  <ul className="space-y-1 text-sm text-muted-foreground">
                    <li className="flex items-start gap-2">
                      <div className="h-1.5 w-1.5 rounded-full bg-current mt-1.5 flex-shrink-0" />
                      <span>Select specific items or categories</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <div className="h-1.5 w-1.5 rounded-full bg-current mt-1.5 flex-shrink-0" />
                      <span>Filter by location, supplier, or value</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <div className="h-1.5 w-1.5 rounded-full bg-current mt-1.5 flex-shrink-0" />
                      <span>Perfect for ad-hoc cycle counts</span>
                    </li>
                  </ul>
                </div>
              </Label>
            </div>
          </RadioGroup>
        </CardContent>
      </Card>

      {/* ABC-Based Configuration */}
      {countType === "abc-based" && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>ABC Classification Settings</CardTitle>
            <CardDescription>
              Configure which ABC class to count
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="abcClass">ABC Class to Count</Label>
              <Combobox
                options={[
                  { value: "A", label: "Class A - High-Value Items", description: "Top 80% of value" },
                  { value: "B", label: "Class B - Medium-Value Items", description: "Next 15% of value" },
                  { value: "C", label: "Class C - Low-Value Items", description: "Last 5% of value" },
                  { value: "ALL", label: "All Classes", description: "Due items only" },
                ]}
                value={formData.abcClass}
                onValueChange={(value) => setFormData({ ...formData, abcClass: value })}
                placeholder="Select ABC class..."
                searchPlaceholder="Search ABC classes..."
                emptyText="No ABC class found."
              />
              <p className="text-xs text-muted-foreground">
                Only items due for counting based on their frequency will be included
              </p>
            </div>

            <div className="rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 p-4">
              <div className="flex items-start gap-3">
                <Info className="h-5 w-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
                <div className="space-y-2 text-sm">
                  <p className="font-medium text-blue-900 dark:text-blue-100">
                    ABC Classification Integration
                  </p>
                  <p className="text-blue-700 dark:text-blue-300">
                    The system will automatically include items that are due for counting based on their ABC classification and the frequencies configured in <strong>Settings → Stock Options</strong>.
                  </p>
                  <p className="text-blue-700 dark:text-blue-300">
                    Configure ABC classifications in <strong>AI Forecasting & Analytics → ABC Classification</strong>.
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* General Settings */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Cycle Count Details</CardTitle>
          <CardDescription>
            Basic information about this cycle count
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="description">
              Description <span className="text-destructive">*</span>
            </Label>
            <Input
              id="description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder={
                countType === "abc-based"
                  ? `${formData.abcClass}-Class Cycle Count - ${format(formData.date, "MMM dd, yyyy")}`
                  : `Manual Cycle Count - ${format(formData.date, "MMM dd, yyyy")}`
              }
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>{startOption === "now" ? "Count Date" : "Scheduled Date"}</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="w-full justify-start text-left font-normal"
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {format(startOption === "now" ? formData.date : formData.scheduledDate, "PPP")}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={startOption === "now" ? formData.date : formData.scheduledDate}
                    onSelect={(date) => {
                      if (date) {
                        if (startOption === "now") {
                          setFormData({ ...formData, date });
                        } else {
                          setFormData({ ...formData, scheduledDate: date });
                        }
                      }
                    }}
                    disabled={startOption === "scheduled" ? (date) => date < new Date() : undefined}
                  />
                </PopoverContent>
              </Popover>
              {startOption === "scheduled" && (
                <p className="text-xs text-muted-foreground">
                  Cycle count will appear as "Scheduled" until this date
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="warehouse">Warehouse</Label>
              <Combobox
                options={warehouseOptions}
                value={formData.warehouse || "all"}
                onValueChange={(value) => setFormData({ ...formData, warehouse: value === "all" ? "" : value })}
                placeholder="Select or search warehouse"
                searchPlaceholder="Search warehouses..."
                emptyText="No warehouse found."
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="assignedTo">
              <Users className="h-4 w-4 inline mr-1" />
              Assigned To
            </Label>
            <Combobox
              options={userOptions}
              value={formData.assignedTo}
              onValueChange={(value) => setFormData({ ...formData, assignedTo: value })}
              placeholder="Select or search user"
              searchPlaceholder="Search users..."
              emptyText="No user found."
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="notes">Notes (Optional)</Label>
            <Textarea
              id="notes"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder="Add any notes or special instructions for this cycle count"
              rows={3}
            />
          </div>
        </CardContent>
      </Card>

      {/* Action Buttons */}
      <div className="flex items-center justify-between pt-6 border-t">
        <Button
          variant="outline"
          onClick={() => navigateTo("stock-takes-home")}
        >
          Cancel
        </Button>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={handleSaveAndExit}
            disabled={!formData.description}
            className="gap-2"
          >
            <Save className="h-4 w-4" />
            Save and Exit
          </Button>
          <Button
            onClick={handleContinue}
            disabled={!formData.description}
            className="gap-2"
          >
            {startOption === "now" ? (
              <>
                Continue to Count
                <ArrowRight className="h-4 w-4" />
              </>
            ) : (
              <>
                <Clock className="h-4 w-4" />
                Schedule Cycle Count
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
